import { useState } from "react";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { BigX } from "@/frontend/SVGs/BigX";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { OPERAND_TYPE, ComparisonRuleType } from "@/types/validationTypes";
import { useShallow } from "zustand/react/shallow";
import { ButtonParent } from "../buttons/ButtonParent";
import { CustomFieldInput } from "../customFIelds/CustomFieldInput";
import { comparisonCharaterFromRuleType, ruleTypeFromChar } from "./utils";
import { Validation } from "@/backend/models/validation";
import { Form } from "react-bootstrap";
import { Parameter } from "@/backend/models/parameter";
import { AssignValueFromSelect } from "../parameters/AssignValueFromSelect";
import { useGetRefAndParameterByConsumer, useParameterHasCorrectSubject } from "@/frontend/hooks/parameters";


interface ValidationEditProps {
  validation: Validation;
  owningParameter: Parameter;
}

export default function ValidationItemEdit({ validation, owningParameter }: ValidationEditProps) {
  const selectedRuleChar = comparisonCharaterFromRuleType(validation.ruleType);
  const validationUpdate = useParameterStore(state => state.updateValidation);
  const deleteValidation = useParameterStore(state => state.deleteValidation);
  const parameters = useParameterStore(useShallow(state => state.parameters.filter(p => p.subjectId === validation.id)));
  const comparisonParameter = parameters.find((p) => p.identifier === OPERAND_TYPE.OPERAND_B);
  const comparisonField = comparisonParameter?.fields[0];
  const consumerReference = useGetRefAndParameterByConsumer(comparisonParameter?.id || '');
  const inputField = parameters.find((p) => p.identifier === OPERAND_TYPE.OPERAND_A)?.fields[0];
  const [failMessage, setFailMessage] = useState(validation.failMessage);

  const handleRuleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    void validationUpdate({ validationParams: { ...validation, ruleType: ruleTypeFromChar(event.target.value) } });
  };

  const handleFailMessageChange = (value: string) => {
    setFailMessage(value);
  };

  const onBlurFailMessage = () => {
    void validationUpdate({ validationParams: { ...validation, failMessage } });
  };

  const handleDeleteValidation = () => {
    void deleteValidation(validation.id);
  };

  if (![CUSTOM_FIELD_TYPES.Float, CUSTOM_FIELD_TYPES.Integer, CUSTOM_FIELD_TYPES.String, CUSTOM_FIELD_TYPES.Boolean].includes(owningParameter.fieldType)) {
    return null;
  }

  // Define which operators are valid for each field type
  const isNumericType = [CUSTOM_FIELD_TYPES.Float, CUSTOM_FIELD_TYPES.Integer].includes(owningParameter.fieldType);
  const availableOperators = isNumericType
    ? [
        ComparisonRuleType.LESS_THAN,
        ComparisonRuleType.LESS_THAN_OR_EQUAL,
        ComparisonRuleType.GREATER_THAN,
        ComparisonRuleType.GREATER_THAN_OR_EQUAL,
        ComparisonRuleType.EQUAL,
        ComparisonRuleType.NOT_EQUAL
      ]
    : [
        ComparisonRuleType.EQUAL,
        ComparisonRuleType.NOT_EQUAL
      ];

  return (
    <div className='border rounded p-2 bg-white shadow-sm'>
      <div className='d-flex align-items-end gap-2'>
        <div className='d-flex align-items-center text-muted flex-shrink-0'>
          <span className='font-monospace small text-truncate mw-20'>
            {inputField?.customValue?.toString() || `<${I18n.get('userInputValue')}>`}
          </span>
        </div>
        <div style={{minWidth: '100px', maxWidth: '120px'}}>
          <Form.Select
            size="sm"
            onChange={handleRuleTypeChange}
            title={I18n.get('updateOperator')}
            value={selectedRuleChar}
            aria-label={I18n.get('updateOperator')}
          >
            {availableOperators.map(operator => (
              <option key={operator} value={comparisonCharaterFromRuleType(operator)}>
                {comparisonCharaterFromRuleType(operator)}
              </option>
            ))}
          </Form.Select>
        </div>
        <div style={{minWidth: '100px', maxWidth: '120px'}}>
          {comparisonField && (
            <div className='d-flex align-items-end gap-2'>
              <CustomFieldInput
                customField={comparisonField}
                label=''
                isEditView={false}
                isInput={false}
                readOnly={!!consumerReference.currentReference}
              />
              <ParameterSelect comparisonParameter={comparisonParameter} owningParameter={owningParameter} />
          </div>
          )}
        </div>
        <div className='d-flex justify-content-end w-100'>
          <ButtonParent
            onClick={handleDeleteValidation}
            title={I18n.get('deleteValidation')}
            aria-label={I18n.get('deleteValidation')}
            className="btn btn-sm btn-danger"
          >
            <BigX />
          </ButtonParent>
        </div>
      </div>
      <Form.Label className="col-form-label col-form-label-sm">{I18n.get('failMessage')}</Form.Label>
      <Form.Control
        as="textarea"
        aria-label={I18n.get('failMessage')}
        defaultValue={failMessage}
        onChange={(event) => handleFailMessageChange(event.target.value)}
        onBlur={onBlurFailMessage}
      />
    </div>
  );
}

function ParameterSelect(props: { comparisonParameter: Parameter, owningParameter: Parameter }) {
  const { comparisonParameter, owningParameter } = props;
  const parameterHasCorrectSubject = useParameterHasCorrectSubject(owningParameter, owningParameter.subjectId);
  const parameters = useParameterStore(state => state.parameters).filter(p =>
    p.type !== owningParameter.type &&
    parameterHasCorrectSubject(p)
  );

  return (
    <AssignValueFromSelect
      parameter={comparisonParameter}
      selectableParameters={parameters}
    />
  );
}
